
import { ServiceLandingPage } from "@/components/service/ServiceLandingPage";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Contractor } from "@/types/contractor";
import { ContractorsList } from "@/components/ContractorsList";
import { LoadingState } from "@/components/LoadingState";
import { useState } from "react";
import { Hero } from "@/components/Hero";

const BestTreeServicesPage = () => {
  const serviceType = "Tree Service";
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  
  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'tree-service'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'Tree Service')
        .eq('location', 'oklahoma-city')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }

      return data as Contractor[];
    },
  });

  const features = [
    {
      title: "Tree Removal",
      description: "Safe removal of dead, damaged, or unwanted trees"
    },
    {
      title: "Tree Trimming & Pruning",
      description: "Professional pruning to improve tree health and appearance"
    },
    {
      title: "Stump Grinding",
      description: "Complete stump removal to reclaim your yard space"
    },
    {
      title: "Emergency Services",
      description: "24/7 response for storm damage and hazardous situations"
    },
    {
      title: "Tree Health Assessment",
      description: "Expert diagnosis of tree diseases and treatment options"
    }
  ];

  const processSteps = [
    {
      title: "Free Consultation",
      description: "Professional assessment of your tree care needs"
    },
    {
      title: "Detailed Quote",
      description: "Clear, written estimate with no hidden fees"
    },
    {
      title: "Professional Service",
      description: "ISA-certified arborists using industry-best practices"
    },
    {
      title: "Complete Cleanup",
      description: "Thorough removal of debris and branches"
    }
  ];

  const benefits = [
    {
      title: "Certified Experts",
      description: "Trained arborists with extensive tree care knowledge"
    },
    {
      title: "Proper Equipment",
      description: "Specialized tools for safe and efficient tree work"
    },
    {
      title: "Insurance Protection",
      description: "Fully insured services to protect your property"
    },
    {
      title: "Enhanced Property Value",
      description: "Well-maintained trees increase your home's curb appeal"
    },
    {
      title: "Property Safety",
      description: "Remove hazardous trees that threaten your home"
    }
  ];

  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: "Tree Services", href: "/best-tree-services-okc" },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Tree Services in Oklahoma City",
    "description": "Professional tree services in Oklahoma City for residential and commercial properties.",
    "provider": {
      "@type": "Organization",
      "name": "Home Pros OKC"
    },
    "areaServed": {
      "@type": "City",
      "name": "Oklahoma City"
    },
    "serviceType": "Tree Service"
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Best Tree Services in Oklahoma City | Home Pros OKC"
        description="Connect with top-rated tree service companies in Oklahoma City for tree removal, trimming, pruning, and emergency services."
        canonicalUrl="/best-tree-services-okc"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title="Best Tree Services in Oklahoma City"
          subtitle="Find Oklahoma City's most trusted arborists and tree care professionals for all your tree service needs."
          buttonText="Get a Free Quote"
          buttonLink="#quote-form"
        />

        <ServiceLandingPage
          title="Best Tree Services in OKC"
          metaDescription="Connect with top-rated tree service companies in Oklahoma City for tree removal, trimming, pruning, and emergency services."
          introText="Find Oklahoma City's most trusted arborists and tree care professionals for all your tree service needs."
          features={features}
          processSteps={processSteps}
          benefits={benefits}
          ctaText="Need professional tree care?"
          ctaButtonText="Get Connected"
          contactText="Fill out the form below to connect with top-rated tree service companies in OKC."
        />

        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Top Tree Service Professionals in Oklahoma City</h2>
          
          {isLoading ? (
            <LoadingState message="Loading tree service professionals..." />
          ) : contractors && contractors.length > 0 ? (
            <ContractorsList
              contractors={contractors}
              isLoading={isLoading}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              onServiceTypeChange={() => {}}
              onLocationChange={() => {}}
              selectedServiceType="Tree Service"
            />
          ) : (
            <p className="text-center text-muted-foreground py-8">
              We're currently updating our list of tree service professionals in Oklahoma City. 
              Please check back soon or use the form above to request a quote.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BestTreeServicesPage;
